import { useCallback } from 'react';
import {
  executeBusinessFieldSave,
  executeBusinessFieldsBatchSave,
  resolveBusinessIdForFieldSave,
  resolveBusinessIdForPersistence
} from '../../../domain/persistence/businessFieldSaveOrchestrator';

export function useBusinessFieldPersistence({
  currentBusinessId,
  businessIdRef,
  scheduleReportSyncAfterSave
}) {
  const resolveBusinessIdForSave = useCallback(
    () => resolveBusinessIdForPersistence(currentBusinessId, businessIdRef),
    [currentBusinessId, businessIdRef]
  );

  const saveBusinessData = useCallback(
    async (field, value, options = {}) => {
      const businessId = resolveBusinessIdForFieldSave(currentBusinessId, businessIdRef);
      try {
        await executeBusinessFieldSave(businessId, field, value, options, {
          onAfterSuccess: scheduleReportSyncAfterSave
            ? (bid) => void scheduleReportSyncAfterSave(bid)
            : undefined
        });
      } catch (error) {
        console.error('Error saving business data:', error);
      }
    },
    [currentBusinessId, businessIdRef, scheduleReportSyncAfterSave]
  );

  const enqueueBusinessFieldSave = useCallback(
    (field, value) => {
      const bid = resolveBusinessIdForSave();
      if (!bid) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[DataContext] ${field} persist skipped: no businessId`);
        }
        return;
      }
      void saveBusinessData(field, value).catch((err) => {
        console.error(`[DataContext] ${field} persist failed:`, err?.message || err);
      });
    },
    [resolveBusinessIdForSave, saveBusinessData]
  );

  const enqueueBusinessFieldsBatchSave = useCallback(
    (fields) => {
      const bid = resolveBusinessIdForSave();
      if (!bid) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[DataContext] batch persist skipped: no businessId', Object.keys(fields || {}));
        }
        return;
      }
      void executeBusinessFieldsBatchSave(bid, fields).catch((err) => {
        console.error('[DataContext] batch persist failed:', err?.message || err);
      });
    },
    [resolveBusinessIdForSave]
  );

  return {
    saveBusinessData,
    enqueueBusinessFieldSave,
    enqueueBusinessFieldsBatchSave,
    resolveBusinessIdForSave
  };
}
